import { action, computed, makeObservable, observable } from 'mobx'

import { LocationType } from '~/client/graph/types.generated'
import IFormFilterFieldOption from '~/client/src/desktop/interfaces/IFormFilterFieldOption'
import { getDisplayHierarchyChains } from '~/client/src/shared/components/ObjectReference/LocationReference'
import locationTypesList from '~/client/src/shared/constants/locationTypesList'
import {
  equipmentPermitFieldTypes,
  materialResourcePermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import { evaluateFilters } from '~/client/src/shared/utils/filterEvaluationHelpers'

import DynamicFiltersStore from '../../DynamicFilters.store'

export interface IFilterOptionItem {
  id: string
  name: string
  isDeleted?: boolean
  stxResourceId?: string
  iconName?: string
  color?: string
}

export interface IExpandedFilterOption {
  location: IFilterOptionItem
  hierarchy: string[]
}

export default class DynamicFiltersListStore {
  @observable public isInactiveExpanded = false

  @observable private fieldId: string
  @observable private availableFilterFieldsOptions: IFormFilterFieldOption[]
  @observable private filtersStore: DynamicFiltersStore
  @observable public materialResourceRecords: IResourceRecord[] = []
  @observable public isConfiguredFieldMaterialResource = false
  @observable.ref private disabledResourceIds: Set<string> = new Set()

  public constructor(
    fieldId: string,
    availableFilterFieldsOptions: IFormFilterFieldOption[],
    filtersStore: DynamicFiltersStore,
    private readonly locationAttributesStore: LocationAttributesStore,
    private readonly tagsStore: TagsStore,
  ) {
    this.updateFieldId(fieldId)
    this.updateAvailableFields(availableFilterFieldsOptions)
    this.updateFiltersStore(filtersStore)

    makeObservable(this)
  }

  @action.bound
  public updateFieldId(fieldId: string) {
    this.fieldId = fieldId
  }

  @action.bound
  public updateAvailableFields(
    availableFilterFieldsOptions: IFormFilterFieldOption[],
  ) {
    this.availableFilterFieldsOptions = availableFilterFieldsOptions
  }

  @action.bound
  public updateFiltersStore(filtersStore: DynamicFiltersStore) {
    this.filtersStore = filtersStore
  }

  @action.bound
  public updateMaterialResourceRecords(records: IResourceRecord[]) {
    this.materialResourceRecords = records
  }

  @action.bound
  public updateIsConfiguredFieldMaterialResource(value: boolean) {
    this.isConfiguredFieldMaterialResource = value
  }

  @action.bound
  public updateDisabledResourceIds(ids?: Set<string>) {
    this.disabledResourceIds = ids ?? new Set()
  }

  @action.bound
  public toggleInactiveExpanded() {
    this.isInactiveExpanded = !this.isInactiveExpanded
  }

  @computed
  public get filterFieldsOptions(): IFormFilterFieldOption[] {
    return this.availableFilterFieldsOptions.filter(
      field => field.field?.id !== this.fieldId,
    )
  }

  @computed
  public get isMaterialResourceField(): boolean {
    return materialResourcePermitFieldTypes.includes(
      this.filtersStore.selectedField?.type,
    )
  }

  @computed
  private get materialResourceRecordIds(): Set<string> {
    return new Set(
      this.materialResourceRecords.filter(r => !r.isDeleted).map(r => r.id),
    )
  }

  private isFilterValueValid(filter: {
    fieldId?: string
    fieldValues?: string[]
  }): boolean {
    const { filterFieldsMap } = this.filtersStore
    if (!filterFieldsMap?.has(filter.fieldId)) return false

    const filterField = filterFieldsMap.get(filter.fieldId)
    if (materialResourcePermitFieldTypes.includes(filterField?.type)) {
      return (
        filter.fieldValues?.some(id =>
          this.materialResourceRecordIds.has(id),
        ) ?? false
      )
    }

    const { hierarchyAllAttributesMap } = this.locationAttributesStore
    return (
      filter.fieldValues?.some(locId => hierarchyAllAttributesMap[locId]) ??
      false
    )
  }

  @computed
  public get inactiveFilterIdxs(): number[] {
    if (!this.filtersStore.filterFieldsMap) return []

    return this.filtersStore.filters.reduce((acc, filter, idx) => {
      if (!this.isFilterValueValid(filter)) acc.push(idx)
      return acc
    }, [] as number[])
  }

  @computed
  public get activeFilterIdxs(): number[] {
    if (!this.filtersStore.filterFieldsMap) {
      return this.filtersStore.filters.map((_, idx) => idx)
    }

    return this.filtersStore.filters.reduce((acc, filter, idx) => {
      if (this.isFilterValueValid(filter)) acc.push(idx)
      return acc
    }, [] as number[])
  }

  @computed
  public get allLocations(): LocationBase[] {
    if (!this.filtersStore.selectedField) {
      return []
    }

    if (this.isMaterialResourceField) {
      return []
    }

    if (
      equipmentPermitFieldTypes.includes(this.filtersStore.selectedField.type)
    ) {
      return this.locationAttributesStore.offloadingEquipmentsStore.list || []
    }

    return this.locationAttributesStore.allLocationsWithoutEquipment || []
  }

  @computed
  public get selectedFieldRestrictedTypes(): LocationType[] {
    if (this.isMaterialResourceField) {
      return []
    }

    if (
      equipmentPermitFieldTypes.includes(this.filtersStore.selectedField?.type)
    ) {
      return locationTypesList.filter(
        t => t !== LocationType.OffloadingEquipment,
      )
    }

    return locationTypesList.filter(
      t =>
        t === LocationType.OffloadingEquipment ||
        t === LocationType.ProjectLocation,
    )
  }

  @computed
  public get selectedFieldRestrictedLocIds(): string[] {
    if (!this.filtersStore.selectedField) {
      return []
    }

    const disabled = [...this.disabledResourceIds]

    // The material trigger's allow-list is applied elsewhere; here we only
    // additionally hide disabled resources.
    if (this.isMaterialResourceField) {
      return disabled
    }

    const restricted = this.allLocations.reduce((acc, location) => {
      const isAllowed = evaluateFilters(
        this.filtersStore.selectedField.filterOptions,
        location,
      )
      if (!isAllowed) {
        acc.push(location.id)
      }
      return acc
    }, [] as string[])

    return [...restricted, ...disabled]
  }

  @computed
  public get expandedFilterOptionsList(): IExpandedFilterOption[] {
    let valuesSource: string[] = []

    if (
      this.filtersStore.isCreationMode ||
      (this.filtersStore.editingFilterIdx !== null &&
        this.filtersStore.editingFilterIdx > -1)
    ) {
      valuesSource = this.filtersStore.formData.values || []
    } else if (
      this.filtersStore.expandedFilterIdx !== null &&
      this.filtersStore.expandedFilterIdx > -1
    ) {
      valuesSource =
        this.filtersStore.filters[this.filtersStore.expandedFilterIdx]
          ?.values || []
    }

    if (this.isConfiguredFieldMaterialResource) {
      const recordsMap = new Map(
        this.materialResourceRecords.map(r => [r.id, r]),
      )
      return valuesSource.map(recordId => {
        const record = recordsMap.get(recordId)
        return {
          location: record
            ? {
                id: record.id,
                name: record.name,
                isDeleted: record.isDeleted,
                stxResourceId: record.stxResourceId,
              }
            : {
                id: recordId,
                name: Localization.translator.unknownDeletedObject,
              },
          hierarchy: [],
        }
      })
    }

    return valuesSource.map(locationId => {
      const location =
        this.locationAttributesStore.allItemsMapWithDeleted[locationId]

      if (!location) {
        return {
          location: {
            id: locationId,
            name: Localization.translator.unknownDeletedObject,
          },
          hierarchy: [],
        }
      }

      return {
        location,
        hierarchy: location.isDeleted
          ? []
          : getDisplayHierarchyChains(
              location,
              this.tagsStore?.tagStoreByTagTypeMap,
            ),
      }
    })
  }
}
